import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import Skeleton from "react-loading-skeleton";

import { PaymentsOrder, PaymentsOrders } from "~/api/Api";

import NoData from "../../containers/NoData";
import DynamicRowTooltip from "../../ui/dynamicRowTooltip/DynamicRowTooltip";
import { Pagination, PaginationConfig } from "../../ui/pagination/Pagination";
import styles from "./Orders.module.scss";

export const getStatusClass = (status: string) => {
  switch (status) {
    case "succeeded":
    case "active":
    case "completed":
    case "complete":
      return styles.statusGreen;
    case "canceled":
      return styles.statusRed;
    default:
      return styles.statusBlue;
  }
};

type OrdersProps = {
  data: PaymentsOrders;
  isLoading: boolean;
  pagination: PaginationConfig;
  setPagination: (pagination: PaginationConfig) => void;
};
const columnHelper = createColumnHelper<PaymentsOrder>();

export const serviceTitle: { [key: string]: string } = {
  brand: "Foundation Links",
  niche: "Authority Links",
  concierge: "Concierge Installation",
  llm_citations: "LLM Citations",
  community: "Community Links",
};

const columns = [
  columnHelper.accessor("service_type", {
    header: "Service Name",
    cell: (info) => serviceTitle?.[info.getValue()] || info.getValue(),
  }),
  columnHelper.accessor("status", {
    header: "Payment",
    cell: (info) => (
      <span
        className={clsx(
          styles.status,
          `${getStatusClass(info.getValue().toLowerCase())}`,
        )}
      >
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("state", {
    header: "Status",
    cell: (info) => (
      <span
        className={clsx(
          styles.status,
          `${getStatusClass(info.getValue().toLowerCase())}`,
        )}
      >
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("created_at", {
    header: "Order Date",
    cell: (info) => new Date(info.getValue()).toLocaleDateString(),
  }),
  columnHelper.accessor("sites", {
    header: "Sites",
    cell: (info) => {
      const sites = info.getValue();

      return (
        <div className={styles.sites}>
          {sites.length > 0 && <DynamicRowTooltip items={sites} />}
        </div>
      );
    },
  }),
  columnHelper.accessor("amount", {
    header: "Order Sum",
    cell: (info) => `$${(info.getValue() / 100).toFixed(2)}`,
  }),
];

const OrdersTable = ({
  data,
  isLoading,
  pagination,
  setPagination,
}: OrdersProps) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  if (!isLoading && data.length === 0) {
    return <NoData title="No orders found" />;
  }

  return (
    <>
      <div className={styles.ordersTable}>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    {columns.map((_, colIndex) => (
                      <td key={colIndex}>
                        <Skeleton />
                      </td>
                    ))}
                  </tr>
                ))
              : table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      <Pagination
        {...pagination}
        isLoading={isLoading}
        totalItems={pagination.totalItems}
        onPageChange={(page) => {
          setPagination({ ...pagination, currentPage: page });
        }}
        onItemsPerPageChange={(items) => {
          setPagination({ ...pagination, itemsPerPage: items, currentPage: 1 });
        }}
      />
    </>
  );
};

export default OrdersTable;
